"use client";

import type { AirdropFarmingMinCapitalResponse } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

type UseMinCapitalParams = {
  leverage: number;
  maxLossUsdc: string;
  numLevels?: number;
  pair?: string;
  exchangeId?: string;
  botType?: string;
};

/**
 * Fetches the minimum capital required from the backend (single source of truth).
 * Requires pair + exchangeId so the backend can compute szDecimals-aware minimums
 * (e.g. 1 HYPE = $33 with szDecimals=0 needs higher min than $10 default).
 */
export function useMinCapital(params: UseMinCapitalParams) {
  const { auth } = useHubUser();
  const apiHelper = useApi();

  const { leverage, maxLossUsdc, numLevels, pair, exchangeId, botType } = params;
  const enabled = leverage > 0 && !!maxLossUsdc && parseFloat(maxLossUsdc) > 0 && !!pair && !!exchangeId;

  return useQuery<AirdropFarmingMinCapitalResponse>({
    queryKey: ["airdrop-farming", "min-capital", leverage, maxLossUsdc, numLevels, pair, exchangeId, botType],
    queryFn: () => {
      if (!pair || !exchangeId) throw new Error("pair and exchangeId are required");

      const queryParams: Record<string, string> = {
        leverage: String(leverage),
        maxLossUsdc,
        pair,
        exchangeId,
      };
      if (numLevels != null) queryParams.numLevels = String(numLevels);
      if (botType != null) queryParams.botType = botType;

      return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AIRDROP_FARMING_MIN_CAPITAL, { queryParams });
    },
    enabled,
    staleTime: 30_000,
  });
}
